/**
 * useActionBarDragDrop.ts
 *
 * Custom hook encapsulating all drag-and-drop logic for the ActionBar.
 * Separated from rendering to keep component responsibilities clean.
 */

import { useState, useCallback, useRef } from 'react';
import { gameLogger } from '../../../../utils/GameLogger';
import type { ActionBarAssignment } from '../../../../shared/types/Input';
import type { SlotRef } from '../../../../shared/hotbar/barLayout';
import { isShiftDragBypassActive } from './hotbarDragLockPref';

/** Shared across mounted ActionBar instances so dropping from Bar 1 onto Bar 2 does not clear Bar 1 on dragend. */
let droppedOnAnyActionBar = false;

export interface DragDropState {
  /** Global drag is happening anywhere over the page */
  isGlobalDrag: boolean;
  /** The single slot index currently being hovered during a drag */
  dragOverSlot: number | null;
  /** Whether the current dragged data is a valid action-bar assignment */
  isDragValid: boolean;
}

export interface DragDropHandlers {
  onDragStart: (slotIndex: number, assignment?: ActionBarAssignment) => (e: React.DragEvent) => void;
  onGlobalDragStart: () => void;
  onDragOver: (slotIndex: number) => (e: React.DragEvent) => void;
  onDragLeave: (slotIndex: number) => (e: React.DragEvent) => void;
  onDrop: (slotIndex: number) => (e: React.DragEvent) => void;
  onDragEnd: () => void;
}

function parseSourceRef(raw: string): SlotRef | null {
  if (!raw) return null;
  const parts = raw.split(':').map((n) => parseInt(n, 10));
  if (parts.length !== 3 || parts.some((n) => Number.isNaN(n))) return null;
  return { bar: parts[0], page: parts[1], slot: parts[2] };
}

export interface ActionBarDragDropOptions {
  /** When true, dragging an existing slot requires holding Shift (or unlocking the bar). */
  dragLocked?: boolean;
  /** Called when the player attempts to drag a slot while `dragLocked` is true without holding Shift. */
  onLockedDragAttempt?: () => void;
}

export function useActionBarDragDrop(
  onSlotUpdate?: (slotIndex: number, assignment: ActionBarAssignment | null) => void,
  /** A drop of one slot onto another: the two swap, in one server message (D2). */
  onSlotMove?: (from: number | SlotRef, to: number | SlotRef) => void,
  barPage: { bar: number; page: number } = { bar: 1, page: 1 },
  options?: ActionBarDragDropOptions,
): [DragDropState, DragDropHandlers] {
  const [isGlobalDrag, setIsGlobalDrag] = useState(false);
  const [dragOverSlot, setDragOverSlot] = useState<number | null>(null);
  const [isDragValid, setIsDragValid] = useState(false);
  const dragCounterRef = useRef(0); // tracks enter/leave nesting
  const activeDragSourceSlotRef = useRef<number | null>(null);
  const droppedOnTargetRef = useRef(false);
  const dragStartedWithShiftRef = useRef(false);
  const dragLocked = options?.dragLocked ?? false;
  const onLockedDragAttempt = options?.onLockedDragAttempt;

  const onDragStart = useCallback(
    (slotIndex: number, assignment?: ActionBarAssignment) => (e: React.DragEvent) => {
      dragStartedWithShiftRef.current = isShiftDragBypassActive(e);
      activeDragSourceSlotRef.current = slotIndex;
      droppedOnTargetRef.current = false;
      droppedOnAnyActionBar = false;
      if (assignment) {
        const id = assignment.type === 'ability' ? assignment.abilityId : assignment.itemId;
        e.dataTransfer.setData('text/plain', `${assignment.type}:${id}`);
        e.dataTransfer.setData('source-slot', slotIndex.toString());
        e.dataTransfer.setData('source-slot-ref', `${barPage.bar}:${barPage.page}:${slotIndex}`);
        e.dataTransfer.effectAllowed = 'move';
        gameLogger.debug('ui', `[ActionBar] Drag start ${assignment.type}:${id} from bar ${barPage.bar} page ${barPage.page} slot ${slotIndex}`);
      }
      setIsGlobalDrag(true);
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('helix:action-drag-start'));
      }
    },
    [barPage.bar, barPage.page]
  );

  const onGlobalDragStart = useCallback(() => {
    setIsGlobalDrag(true);
  }, []);

  const onDragOver = useCallback(
    (slotIndex: number) => (e: React.DragEvent) => {
      e.preventDefault();
      // Inventory items are copied onto the bar (the item remains in the
      // bag); action-bar assignments are moved between slots. Matching the
      // producer's effectAllowed is required for real Chromium drops.
      const types = Array.from(e.dataTransfer.types);
      e.dataTransfer.dropEffect = types.includes('source-slot') ? 'move' : 'copy';

      // Only log on class change to avoid spam
      if (dragOverSlot !== slotIndex) {
        gameLogger.debug('ui', `[ActionBar] DragOver slot ${slotIndex}`);
      }

      setDragOverSlot(slotIndex);

      // Browsers do not expose payload contents until drop, but all supported
      // ability/item producers publish text/plain.
      const valid = types.includes('text/plain');
      setIsDragValid(valid);
    },
    [dragOverSlot]
  );

  const onDragLeave = useCallback(
    (slotIndex: number) => (e: React.DragEvent) => {
      e.preventDefault();
      gameLogger.debug('ui', `[ActionBar] DragLeave slot ${slotIndex}`);
      setDragOverSlot(prev => (prev === slotIndex ? null : prev));
    },
    []
  );

  const onDrop = useCallback(
    (slotIndex: number) => (e: React.DragEvent) => {
      e.preventDefault();
      droppedOnTargetRef.current = true;
      droppedOnAnyActionBar = true;
      gameLogger.debug('ui', `[ActionBar] Drop triggered on slot ${slotIndex}`);

      setDragOverSlot(null);
      setIsGlobalDrag(false);
      setIsDragValid(false);
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('helix:action-drag-end'));
      }

      const data = e.dataTransfer.getData('text/plain');
      const sourceSlotRaw = e.dataTransfer.getData('source-slot');
      const sourceRefRaw = e.dataTransfer.getData('source-slot-ref');

      gameLogger.debug('ui', `[ActionBar] Drop data: "${data}", source slot: "${sourceSlotRaw}", ref: "${sourceRefRaw}"`);

      const separator = data.indexOf(':');
      const kind = data.slice(0, separator);
      const id = data.slice(separator + 1);
      const assignment: ActionBarAssignment | null = kind === 'ability' && id
        ? { type: 'ability', abilityId: id }
        : kind === 'item' && id
          ? { type: 'item', itemId: id }
          : null;

      if (assignment) {
        gameLogger.info('ui', `[ActionBar] Dropped ${kind}:${id} onto bar ${barPage.bar} page ${barPage.page} slot ${slotIndex}`);

        const sourceRef = parseSourceRef(sourceRefRaw);
        const targetRef: SlotRef = { bar: barPage.bar, page: barPage.page, slot: slotIndex };
        const sourceSlotIndex = sourceRef?.slot ?? (sourceSlotRaw ? parseInt(sourceSlotRaw, 10) : NaN);
        const sameBarAndPage = !sourceRef || (sourceRef.bar === targetRef.bar && sourceRef.page === targetRef.page);
        const sameSlot = sameBarAndPage && sourceSlotIndex === slotIndex;

        // From another action bar slot (same bar or across bars/pages): one move, and the two slots swap.
        if (!isNaN(sourceSlotIndex) && !sameSlot && onSlotMove) {
          const useFullRef = (sourceRef && (sourceRef.bar !== 1 || sourceRef.page !== 1))
            || targetRef.bar !== 1
            || targetRef.page !== 1;
          if (useFullRef) {
            onSlotMove(sourceRef ?? { bar: 1, page: 1, slot: sourceSlotIndex }, targetRef);
          } else {
            onSlotMove(sourceSlotIndex, slotIndex);
          }
          return;
        }
        onSlotUpdate?.(slotIndex, assignment);
      } else {
        gameLogger.warn('ui', `[ActionBar] Drop event with unexpected data: "${data}"`);
      }
    },
    [onSlotUpdate, onSlotMove, barPage.bar, barPage.page]
  );

  const onDragEnd = useCallback(() => {
    gameLogger.debug('ui', `[ActionBar] DragEnd globally`);
    setIsGlobalDrag(false);
    setDragOverSlot(null);
    setIsDragValid(false);
    dragCounterRef.current = 0;

    // If dragged off the action bar and released into empty space / non-slot target,
    // remove the assignment from the source slot (plan / DJ: drag and drop off to remove).
    // Locked drag-off requires Shift or unlocking the padlock (DJ: keep it locked drag off or with shift in wow).
    if (activeDragSourceSlotRef.current !== null && !droppedOnTargetRef.current && !droppedOnAnyActionBar) {
      if (dragLocked && !dragStartedWithShiftRef.current) {
        gameLogger.info('ui', '[ActionBar] Dragged off while locked — slot preserved');
        onLockedDragAttempt?.();
      } else {
        gameLogger.info('ui', `[ActionBar] Dropped off action bar from slot ${activeDragSourceSlotRef.current} — clearing slot`);
        onSlotUpdate?.(activeDragSourceSlotRef.current, null);
      }
    }
    activeDragSourceSlotRef.current = null;
    droppedOnTargetRef.current = false;
  }, [onSlotUpdate, dragLocked, onLockedDragAttempt]);

  const state: DragDropState = { isGlobalDrag, dragOverSlot, isDragValid };
  const handlers: DragDropHandlers = {
    onDragStart,
    onGlobalDragStart,
    onDragOver,
    onDragLeave,
    onDrop,
    onDragEnd,
  };

  return [state, handlers];
}
